// Shared post-Cognito-login saga logic for both SignIn and MFA flows
import { put, call } from 'redux-saga/effects';
import { ROUTES } from '@shared/constants';
import { signIn } from '@services/authenticationService';
import { getConversationsAction } from '@containers/App/containers/Messaging/actions';
import { changeLocale } from '@containers/LanguageProvider/actions';
import { clearUiState as clearSignInUiState, cognitoSignInSuccess, signInSuccess } from './SignIn/actions';
import { setRedirectUrl } from '@containers/App/components/Redirector/actions';


export function* stopSignInAndRedirect(url, stateToPass) {
  // We're intentionally leaving the Sign In screen, so clear the in-progress state
  // without surfacing an error.
  yield put(clearSignInUiState());
  yield put(setRedirectUrl(url, stateToPass));
}

/**
 * Handles post-Cognito-login actions: persist token, fetch user, set locale, get conversations.
 * @param {Object} params
 * @param {string} params.cognitoSub - Cognito user sub (or email as fallback)
 * @param {string} params.token - Cognito JWT token
 * @param {string} [params.email] - Email (optional, fallback for signIn)
 * @returns {Object} user
 */
export function* handlePostCognitoLogin({ cognitoSub, token, email }) {
  // Persist Cognito JWT so subsequent API requests include it in headers.
  yield put(cognitoSignInSuccess(token));
  const user = yield call(signIn, cognitoSub);

  // Password expiration policy is enforced by the backend after Cognito auth.
  // If expired, redirect to the existing Reset Password screen and trigger the
  // Cognito forgot-password flow (verification code + new password).
  if (user?.requiresPasswordReset) {
    yield* stopSignInAndRedirect(ROUTES.RESET_PASSWORD, {
      email,
      reason: 'passwordExpired',
      autoSendCode: true,
    });
    return;
  }

  if (user?.requiresSystemSignup) {
    yield* stopSignInAndRedirect(ROUTES.COMPLETE_SIGN_UP, {
      email,
      cognitoSub,
    });
    return;
  }
  if (!user) {
    // Ensure the Sign In screen doesn't remain in a "sending" state.
    // Treat this as an error so calling sagas can surface a message.
    throw new Error('User not found');
  }

  yield put(signInSuccess(user));
  yield put(getConversationsAction());
  if (user?.locale) {
    yield put(changeLocale(user.locale.toLowerCase()));
  }
  return user;
}
